import $ from "jquery";
import qs from "query-string";
import Backbone from "backbone";
import Marionette from "backbone.marionette";
import AnimatedRegion from "marionette-animated-region";

import wsStorage from "lib/wsStorage";
import { DEGREE_TYPE } from "lib/constants";

import components from "../components";
import config from "../config";
import steps from "./step";
import models from "../models/user";
import modelUtil from "../models/util";
import util from "../util";
import AddReferenceModal from "../components/reference";
import SkillsStep from "./app/step/skills";

import profileTemplate from "../templates/candidate/profile.jst";

let completeProfileOnPageLoad = null;

const ListView = Marionette.View.extend({
    template: profileTemplate,
    ui: {
        avail: "#availability",
        header: ".profile-header-container",
        profileStatusMobile: ".profile-status-mobile",
    },
    events: {
        "click @ui.profileStatusMobile": "onShowProfileStatus",
        "click .js-add-reference": "onAddReference",
        "click .profile-checklist__im-done": "onDoneAddingJobsClick",
    },
    onShowProfileStatus(event) {
        event.preventDefault();

        const $tooltip = this.ui.profileStatusMobile.find("div");
        if ($(event.target).closest($tooltip).length > 0) {
            return;
        }

        $tooltip.toggle();
    },
    serializeData() {
        const json = modelUtil.formatAppData(this.options.data) || {};

        // Don't show company questions here!
        json.companies = [];

        // Only show education if not answered or > grade 12
        json.showEducationSection = json.schools.length === 0;
        if (json.schools.length > 0) {
            const degreeType = Number(json.schools[0].degree_type);
            json.showEducationSection = degreeType !== DEGREE_TYPE.GRADE_SCHOOL;
        }

        json.hasPrefs = Object.keys(json.user.prefs).length > 0;

        if (json.hasPrefs && json.user.prefs.search_status) {
            const text = modelUtil.CANDIDATE_SEARCH_STATUS_MAP[json.user.prefs.search_status];
            json.user.prefs.statusText = text;
        }

        if (completeProfileOnPageLoad === null || !json.profileCompleteness.allComplete) {
            completeProfileOnPageLoad = json.profileCompleteness.allComplete;
        }
        json.completeProfileOnPageLoad = completeProfileOnPageLoad;

        return json;
    },
    onRender() {
        this.bindUIElements();

        this.availComponent = new components.AvailabilityComponent({
            el: this.ui.avail,
            data: this.serializeData(),
            disabled: true,
        });
        this.availComponent.render();

        if (this.options.showRefModal) {
            this.showReferenceModal();
        }
    },
    onAddReference(event) {
        event.preventDefault();

        this.showReferenceModal();
    },
    showReferenceModal() {
        const modal = new AddReferenceModal({
            data: this.options.data,
        });
        modal.render();
    },
    onDoneAddingJobsClick() {
        wsStorage.setItem("doneAddingJobs", true);
        this.render();
    },
});

const ProfileContainer = Marionette.View.extend({
    template: false,
    regions: {
        stepContainer: {
            selector: "#step-container",
            regionClass: AnimatedRegion,
            animation: {
                showAnimation: [
                    {
                        properties: "transition.slideRightBigIn",
                    },
                ],
                hideAnimation: [
                    {
                        properties: "transition.slideLeftBigOut",
                    },
                ],
            },
        },
    },
    ui: {
        header: ".profile-header",
        buttonContainer: ".next-container",
        loader: ".loader",
        nextButton: ".next-button",
        stepContainer: "#step-container",
    },
    events: {
        "click [data-step]": "loadStep",
    },
    initialize() {
        this.showRefModal = false;
        this.model = new models.User(window.profileData);

        this.steps = {
            STEP_INFO: [steps.BasicInfo],
            STEP_AVAILABILITY: [steps.Availability],
            STEP_HISTORY: [steps.History],
            STEP_SKILLS: [SkillsStep],
            STEP_SUMMARY: [steps.Summary],
            // education: [steps.Education]
            // references: steps.Reference,
        };

        this.currentStep = {};
        this.listenTo(this.model, "step:end", (save) => {
            this.saveProfile(save, true);
        });
        this.listenTo(this.model, "step:save", () => {
            this.saveProfile(true, false);
        });

        Backbone.Radio.channel("profile").on("step:show", (name) => {
            this.showStep(name);
        });

        Backbone.Radio.channel("profile").on("reload", () => {
            this.data = null;
            this.loadProfile();
        });

        Backbone.Radio.channel("profile").on("preferences", (data) => {
            if (this.data && this.data.user) {
                this.data.user.prefs = data;
                this.loadProfile();
            }
            this.model.setShiftPrefs(data);
        });

        const hash = window.location.hash ? window.location.hash.substr(1) : "";
        switch (hash) {
            case "info":
                this.initialStep = "STEP_INFO";
                break;

            case "experience":
            case "history":
                this.initialStep = "STEP_HISTORY";
                break;

            case "skills":
                this.initialStep = "STEP_SKILLS";
                break;

            case "availability":
                this.initialStep = "STEP_AVAILABILITY";
                break;

            case "summary":
                this.initialStep = "STEP_SUMMARY";
                break;

            case "references":
                this.showRefModal = true;
                break;

            default:
                break;
        }

        // if resume upload param set - set step to history
        const query = qs.parse(window.location.search);
        if (query[config.RESUME_GET_PARAM_FOR_RELOAD.KEY]) {
            this.initialStep = "STEP_HISTORY";
        }
    },
    onRender() {
        Backbone.Radio.channel("region").on("region:shown", this.onRegionShown.bind(this));
        this.loadProfile();
    },
    onRegionShown(region) {
        // animated region's transforms mess up position: fixed elements
        region.$el.css("transform", "none");
        this.trigger("region:shown");
    },
    loadStep(e) {
        const $target = $(e.currentTarget);
        const stepName = $target.data("step");

        ProfileContainer.scrollPos = document.body.scrollTop;

        this.showStep(stepName);
    },
    loadProfile(data) {
        this.currentStep = {};
        this.ui.buttonContainer.hide();

        if (data) {
            this.data = data;
        } else if (!this.data) {
            const self = this;

            $.ajax({
                type: "GET",
                url: "/api/v1/profile/",
                dataType: "json",
                contentType: "application/json",
            }).done((result) => {
                self.loadProfile(result);
            });

            return;
        }

        if (this.initialStep) {
            this.showStep(this.initialStep);
            this.initialStep = null;
        } else {
            this.getRegion("stepContainer").reset();

            const listView = new ListView({
                model: this.model,
                data: this.data,
                showRefModal: this.showRefModal,
            });
            this.showChildView("stepContainer", listView);
            this.ui.stepContainer.removeClass("list-container");
        }

        this.showRefModal = false;

        setTimeout(() => {
            $("html, body").animate({ scrollTop: ProfileContainer.scrollPos }, 0);
            window.scrollTo(0, ProfileContainer.scrollPos);
        }, 10);
    },
    showStep(stepName, index) {
        if (!index) {
            index = 0;
        }

        const opts = {
            model: this.model,
            isProfile: true,
        };
        if (stepName === "STEP_HISTORY") {
            opts.collection = this.model.get("jobs");
        } else if (stepName === "STEP_EDUCATION") {
            opts.collection = this.model.get("schools");
        }

        const ViewClass = this.steps[stepName][index];

        const view = new ViewClass(opts);
        if (view.shouldSkip() && index > 0) {
            this.loadProfile();
            return false;
        }

        this.getRegion("stepContainer").reset();
        this.showChildView("stepContainer", new ViewClass(opts));
        this.ui.stepContainer.addClass("list-container");
        this.ui.buttonContainer.fadeIn();

        this.currentStep = {
            name: stepName,
            index,
        };

        return true;
    },
    saveProfile(save, loadProfile) {
        if (!save) {
            this.loadProfile();
            return;
        }

        const self = this;
        $.ajax({
            type: "patch",
            url: "/api/v1/profile/",
            data: JSON.stringify(this.model.toJSON()),
            dataType: "json",
            contentType: "application/json",
        })
            .done((data) => {
                util.showToast();

                if (loadProfile) {
                    const step = self.steps[self.currentStep.name];
                    if (step && self.currentStep.index < step.length - 1) {
                        const shown = self.showStep(
                            self.currentStep.name,
                            self.currentStep.index + 1,
                        );
                        // Only load profile if we don't have another step
                        if (shown) {
                            return;
                        }
                    }
                    self.loadProfile(data);
                }
            })
            .fail(() => {
                self.showConnectionAlert();
            });
    },
    showConnectionAlert() {
        setTimeout(() => {
            /* eslint-disable no-alert */
            alert(
                "Your profile data could not be saved. Please check " +
                    "your internet connection and try again.",
            );
            /* esling-enable */
        }, 150);
    },
});
ProfileContainer.scrollPos = 0;

export default ProfileContainer;
